import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { Plus, Wallet as WalletIcon, ArrowLeftRight, MoreVertical, Archive, Pencil, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import * as Icons from 'lucide-react';

export default function Wallets() {
  const { cardShape } = useTheme();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editWallet, setEditWallet] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'bank_account', icon: 'Wallet', color: '#4F8EF7', currency: 'AOA', initial_balance: 0 });
  const navigate = useNavigate();

  const fetchWallets = async () => {
    setLoading(true);
    const res = await api.get('/wallets');
    setWallets(res.data);
    setLoading(false);
  };

  useEffect(() => { fetchWallets(); }, []);

  const totalBalance = wallets.filter(w => w.include_in_total && !w.is_archived).reduce((s, w) => s + parseFloat(w.current_balance || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editWallet) {
      await api.put(`/wallets/${editWallet.id}`, form);
    } else {
      await api.post('/wallets', form);
    }
    setShowForm(false);
    setEditWallet(null);
    setForm({ name: '', type: 'bank_account', icon: 'Wallet', color: '#4F8EF7', currency: 'AOA', initial_balance: 0 });
    fetchWallets();
  };

  const handleDelete = async (id) => {
    if (confirm('Eliminar esta carteira? As transações associadas serão perdidas.')) {
      await api.delete(`/wallets/${id}`);
      fetchWallets();
    }
  };

  const walletTypes = {
    bank_account: 'Conta Bancária', cash: 'Dinheiro', savings: 'Poupança',
    credit_card: 'Cartão Crédito', investment: 'Investimento', mobile_wallet: 'Carteira Digital',
    crypto: 'Crypto', other: 'Outro',
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><div className="skeleton" style={{ width: 200, height: 28 }} /></div>
        <div className="grid-auto">{[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: 160, borderRadius: 16 }} />)}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Carteiras</h1>
          <p className="page-subtitle">Total: {formatCurrency(totalBalance)}</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditWallet(null); setForm({ name: '', type: 'bank_account', icon: 'Wallet', color: '#4F8EF7', currency: 'AOA', initial_balance: 0 }); setShowForm(true); }}>
          <Plus size={18} /> Nova Carteira
        </button>
      </div>

      <div className="grid-auto">
        {wallets.map((w) => {
          const IconComp = Icons[w.icon] || WalletIcon;
          return (
            <div key={w.id} className={`wallet-card variant-${cardShape}`} onClick={() => navigate(`/wallets/${w.id}`)}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: w.color, opacity: 0.6 }} />
              <div className="wallet-card-icon" style={{ background: `${w.color}22` }}>
                <IconComp size={22} color={w.color} />
              </div>
              <div className="wallet-card-name">{w.name}</div>
              <div className="wallet-card-balance">{formatCurrency(w.current_balance, w.currency)}</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <span className="badge badge-active">{walletTypes[w.type]}</span>
                {w.currency !== 'AOA' && <span className="badge badge-transfer">{w.currency}</span>}
              </div>
              <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 4 }}>
                <button className="btn btn-ghost btn-icon" style={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); setEditWallet(w); setForm(w); setShowForm(true); }}>
                  <Pencil size={14} />
                </button>
                <button className="btn btn-ghost btn-icon" style={{ width: 28, height: 28 }} onClick={(e) => { e.stopPropagation(); handleDelete(w.id); }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de criação/edição */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editWallet ? 'Editar Carteira' : 'Nova Carteira'}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select className="form-input form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      {Object.entries(walletTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Moeda</label>
                    <select className="form-input form-select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                      {['AOA', 'USD', 'EUR', 'GBP', 'BRL', 'ZAR', 'CNY'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Saldo Inicial</label>
                    <input className="form-input form-input-money" type="number" step="0.01" value={form.initial_balance} onChange={e => setForm({ ...form, initial_balance: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cor</label>
                    <input type="color" className="form-input" style={{ height: 42, padding: 4, cursor: 'pointer' }} value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editWallet ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
